import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { clsx } from 'clsx';
import { AlertTriangle, GitMerge, Loader2 } from 'lucide-react';
import { useQualityDashboardStore } from '@/stores/qualityDashboard';
import { useKnowledgeStore } from '@/stores/knowledge';
import type {
  OverMergeCandidate,
  UnderMergeCandidate,
} from '@/services/api';

export function MergeSignalsPanel() {
  const {
    mergeSignals,
    mergeSignalEntityType,
    mergeSigma,
    mergeSimFloor,
    signalsLoading,
    selectedOverMerge,
    selectedUnderMerge,
    fetchMergeSignals,
    setMergeSignalParams,
    selectOverMerge,
    selectUnderMerge,
    triggerMergeForUnderMerge,
  } = useQualityDashboardStore();
  const { entityTypes, fetchEntityTypes } = useKnowledgeStore();

  useEffect(() => {
    fetchEntityTypes();
    fetchMergeSignals();
  }, [fetchMergeSignals, fetchEntityTypes]);

  const over = mergeSignals?.over_merge ?? [];
  const under = mergeSignals?.under_merge ?? [];

  return (
    <div className="space-y-4">
      <div className="bg-white rounded-xl border border-slate-200 p-4 flex items-center gap-3 flex-wrap">
        <AlertTriangle size={12} className="text-amber-500" />
        <select
          value={mergeSignalEntityType}
          onChange={(e) =>
            setMergeSignalParams({ entity_type: e.target.value })
          }
          className="text-xs bg-slate-50 border border-slate-200 rounded-lg px-2 py-1.5"
        >
          <option value="">All entity types</option>
          {entityTypes.map((t) => (
            <option key={t} value={t}>
              {t}
            </option>
          ))}
        </select>
        <label className="flex items-center gap-1 text-xs text-slate-500">
          σ
          <input
            type="number"
            min={0.5}
            max={5}
            step={0.25}
            value={mergeSigma}
            onChange={(e) =>
              setMergeSignalParams({
                sigma_threshold: Number(e.target.value) || 2.0,
              })
            }
            className="w-16 text-xs bg-slate-50 border border-slate-200 rounded px-2 py-1"
          />
        </label>
        <label className="flex items-center gap-1 text-xs text-slate-500">
          sim ≥
          <input
            type="number"
            min={0.7}
            max={0.99}
            step={0.01}
            value={mergeSimFloor}
            onChange={(e) =>
              setMergeSignalParams({
                similarity_floor: Number(e.target.value) || 0.9,
              })
            }
            className="w-16 text-xs bg-slate-50 border border-slate-200 rounded px-2 py-1"
          />
        </label>
        {signalsLoading && (
          <Loader2 size={12} className="animate-spin text-slate-400 ml-auto" />
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Over-merge */}
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <div className="px-4 py-2 border-b border-slate-100 bg-slate-50/60 flex items-center justify-between">
            <div>
              <h3 className="text-xs font-semibold text-navy-900">
                Over-merge candidates
              </h3>
              <p className="text-[10px] text-slate-500">
                source_count &gt; mean + {mergeSigma}σ · {over.length}
              </p>
            </div>
          </div>
          <div className="max-h-[40vh] overflow-auto divide-y divide-slate-100">
            {over.length === 0 ? (
              <div className="px-4 py-6 text-xs text-slate-400 text-center">
                No over-merge signals for this filter.
              </div>
            ) : (
              over.map((c) => (
                <OverMergeRow
                  key={c.id}
                  candidate={c}
                  selected={selectedOverMerge?.id === c.id}
                  onClick={() => selectOverMerge(c)}
                />
              ))
            )}
          </div>
          {selectedOverMerge && <OverMergeDetail candidate={selectedOverMerge} />}
        </div>

        {/* Under-merge */}
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <div className="px-4 py-2 border-b border-slate-100 bg-slate-50/60 flex items-center justify-between">
            <div>
              <h3 className="text-xs font-semibold text-navy-900">
                Under-merge candidates
              </h3>
              <p className="text-[10px] text-slate-500">
                cosine similarity &gt; {mergeSimFloor.toFixed(2)} · {under.length}
              </p>
            </div>
          </div>
          <div className="max-h-[40vh] overflow-auto divide-y divide-slate-100">
            {under.length === 0 ? (
              <div className="px-4 py-6 text-xs text-slate-400 text-center">
                {mergeSignals
                  ? 'No under-merge pairs for this filter (or pgvector unavailable).'
                  : 'Loading...'}
              </div>
            ) : (
              under.map((c) => (
                <UnderMergeRow
                  key={`${c.entity_a_id}:${c.entity_b_id}`}
                  candidate={c}
                  selected={
                    selectedUnderMerge?.entity_a_id === c.entity_a_id &&
                    selectedUnderMerge?.entity_b_id === c.entity_b_id
                  }
                  onClick={() => selectUnderMerge(c)}
                />
              ))
            )}
          </div>
          {selectedUnderMerge && (
            <UnderMergeDetail
              candidate={selectedUnderMerge}
              onMerge={() => triggerMergeForUnderMerge(selectedUnderMerge)}
            />
          )}
        </div>
      </div>
    </div>
  );
}

function OverMergeRow({
  candidate,
  selected,
  onClick,
}: {
  candidate: OverMergeCandidate;
  selected: boolean;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      className={clsx(
        'w-full text-left px-4 py-2.5 transition-colors',
        selected ? 'bg-amber-50 border-l-4 border-l-amber-400' : 'hover:bg-slate-50 border-l-4 border-l-transparent',
      )}
    >
      <div className="flex items-center gap-2">
        <span className="text-sm font-medium text-slate-800 truncate flex-1">
          {candidate.canonical_name}
        </span>
        <span className="text-[10px] px-1.5 py-0.5 rounded border bg-amber-50 text-amber-700 border-amber-200 font-bold uppercase flex-shrink-0">
          {candidate.source_entity_count} sources
        </span>
      </div>
      <div className="flex items-center gap-2 mt-0.5 text-[10px] text-slate-500">
        <span>{candidate.entity_type}</span>
        <span>
          mean {candidate.mean_source_count.toFixed(1)} · σ{' '}
          {candidate.std_source_count.toFixed(1)}
        </span>
      </div>
    </button>
  );
}

function OverMergeDetail({ candidate }: { candidate: OverMergeCandidate }) {
  let sourceNames: string[] = [];
  if (candidate.source_names) {
    try {
      const parsed = JSON.parse(candidate.source_names);
      if (Array.isArray(parsed)) sourceNames = parsed.filter((s): s is string => typeof s === 'string');
    } catch {
      // source_names may be a plain string fallback
      sourceNames = [candidate.source_names];
    }
  }
  return (
    <div className="px-4 py-3 border-t border-slate-200 bg-slate-50/60 space-y-2">
      <div className="flex items-center gap-2 flex-wrap">
        <Link
          to={`/knowledge/estate/entities/${candidate.id}`}
          className="text-xs text-brand-teal hover:text-brand-teal-dark"
        >
          Open canonical →
        </Link>
        <Link
          to={`/knowledge/estate/runs?canonical_id=${encodeURIComponent(candidate.id)}`}
          className="text-xs text-slate-500 hover:text-slate-700"
        >
          Flag for review
        </Link>
      </div>
      {sourceNames.length > 0 && (
        <div>
          <div className="text-[10px] font-bold text-slate-500 uppercase mb-1">
            Absorbed source concepts ({sourceNames.length})
          </div>
          <div className="flex flex-wrap gap-1">
            {sourceNames.slice(0, 20).map((n, i) => (
              <span
                key={i}
                className="text-[10px] px-1.5 py-0.5 rounded-full bg-white border border-slate-200 text-slate-700"
              >
                {n}
              </span>
            ))}
            {sourceNames.length > 20 && (
              <span className="text-[10px] text-slate-400 italic">
                +{sourceNames.length - 20}
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function UnderMergeRow({
  candidate,
  selected,
  onClick,
}: {
  candidate: UnderMergeCandidate;
  selected: boolean;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      className={clsx(
        'w-full text-left px-4 py-2.5 transition-colors',
        selected ? 'bg-rose-50 border-l-4 border-l-rose-400' : 'hover:bg-slate-50 border-l-4 border-l-transparent',
      )}
    >
      <div className="flex items-center gap-2">
        <span className="text-sm font-medium text-slate-800 truncate flex-1">
          {candidate.name_a}
        </span>
        <GitMerge size={11} className="text-slate-400 flex-shrink-0" />
        <span className="text-sm font-medium text-slate-800 truncate flex-1">
          {candidate.name_b}
        </span>
      </div>
      <div className="flex items-center gap-2 mt-0.5 text-[10px] text-slate-500">
        <span>{candidate.entity_type}</span>
        <span>
          sim <strong className="text-rose-600">{(candidate.similarity * 100).toFixed(1)}%</strong>
        </span>
        <span>
          {candidate.source_count_a}+{candidate.source_count_b} sources
        </span>
      </div>
    </button>
  );
}

function UnderMergeDetail({
  candidate,
  onMerge,
}: {
  candidate: UnderMergeCandidate;
  onMerge: () => void;
}) {
  const { actingId } = useQualityDashboardStore();
  const busy = actingId === `${candidate.entity_a_id}:${candidate.entity_b_id}`;
  return (
    <div className="px-4 py-3 border-t border-slate-200 bg-slate-50/60 space-y-2">
      <div className="grid grid-cols-2 gap-2 text-xs">
        <Link
          to={`/knowledge/estate/entities/${candidate.entity_a_id}`}
          className="border border-slate-200 rounded-lg p-2 bg-white hover:border-brand-teal/50"
        >
          <div className="font-medium text-slate-800 truncate">
            {candidate.name_a}
          </div>
          <div className="text-[10px] text-slate-500 mt-0.5">
            {candidate.source_count_a} sources
          </div>
        </Link>
        <Link
          to={`/knowledge/estate/entities/${candidate.entity_b_id}`}
          className="border border-slate-200 rounded-lg p-2 bg-white hover:border-brand-teal/50"
        >
          <div className="font-medium text-slate-800 truncate">
            {candidate.name_b}
          </div>
          <div className="text-[10px] text-slate-500 mt-0.5">
            {candidate.source_count_b} sources
          </div>
        </Link>
      </div>
      <button
        onClick={onMerge}
        disabled={busy}
        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold bg-brand-teal text-navy-900 rounded-lg hover:bg-brand-teal/90 disabled:opacity-40"
      >
        {busy ? (
          <Loader2 size={11} className="animate-spin" />
        ) : (
          <GitMerge size={11} />
        )}
        Trigger LLM resolution on this pair's type
      </button>
    </div>
  );
}
